import React from "react";

import type { CoverageCell, TelemetrySummary } from "@/lib/telemetry";
import { formatNumber, formatTs, type TelemetryTz } from "@/components/telemetry/format";
import { useT } from "@/app/localization";

function Cell({
  label,
  value,
  hint,
  tone,
  testId,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: "warn" | "error";
  testId?: string;
}) {
  const toneClass =
    tone === "error" ? "text-destructive" : tone === "warn" ? "text-amber-600 dark:text-amber-400" : "";
  return (
    <div className="rounded-lg border border-border px-3 py-2" data-testid={testId}>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={`mt-0.5 truncate text-sm font-semibold ${toneClass}`}>{value}</div>
      {hint && <div className="truncate text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

/**
 * The counters, and the honesty about which number is which.
 *
 * "Last archived" is not "last seen": the lake lags, so labelling the newest
 * archived sample as a liveness signal would make a lagging feeder look like a
 * dead charger. Wide windows have no server-side summary, so this falls back to
 * what coverage can actually total and says the events counts are page-scoped.
 */
export function SummaryStrip({
  summary,
  coverage,
  eventCount,
  tz,
}: {
  summary: TelemetrySummary | null;
  coverage: CoverageCell[];
  eventCount: number;
  tz: TelemetryTz;
}) {
  const t = useT();
  const withData = coverage.filter((c) => c.samples > 0).length;
  const notArchived = coverage.filter((c) => c.status === "not_archived").length;
  const granularity = coverage.some((c) => c.hour !== undefined) ? "hour" : "day";
  const coverageValue = t("operator.telemetry.coverage_value", { covered: withData, total: coverage.length, granularity });
  const notArchivedHint = notArchived ? t("operator.telemetry.not_archived_count", { count: notArchived }) : undefined;
  const count = (value: number) => formatNumber(value, 0);

  if (!summary) {
    const samples = coverage.reduce((sum, c) => sum + c.samples, 0);
    const commands = coverage.reduce((sum, c) => sum + c.commands, 0);
    const errors = coverage.reduce((sum, c) => sum + c.errors, 0);
    return (
      <div
        className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5"
        data-testid="telemetry-summary"
      >
        <Cell label={t("operator.telemetry.samples_archived")} value={count(samples)} />
        <Cell
          label={t("operator.telemetry.coverage")}
          value={coverageValue}
          hint={notArchivedHint}
        />
        <Cell label={t("operator.telemetry.commands")} value={count(commands)} />
        <Cell
          label={t("operator.telemetry.error_samples")}
          value={count(errors)}
          tone={errors ? "error" : undefined}
        />
        <Cell label={t("operator.telemetry.events_loaded")} value={count(eventCount)} hint={t("operator.telemetry.this_page_only")} />
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6"
      data-testid="telemetry-summary"
    >
      <Cell
        label={t("operator.telemetry.last_archived")}
        value={formatTs(summary.last_archived_ts, tz)}
        hint={summary.last_state ? t("operator.telemetry.last_state", { state: summary.last_state }) : undefined}
        testId="telemetry-last-archived"
      />
      <Cell
        label={t("operator.telemetry.samples")}
        value={count(summary.samples)}
        hint={
          summary.last_voltage != null || summary.last_current != null
            ? t("operator.telemetry.last_readings", {
                voltage: formatNumber(summary.last_voltage),
                current: formatNumber(summary.last_current),
              })
            : undefined
        }
      />
      <Cell
        label={t("operator.telemetry.coverage")}
        value={coverageValue}
        hint={notArchivedHint}
      />
      <Cell label={t("operator.telemetry.state_changes")} value={count(summary.state_changes)} />
      <Cell
        label={t("operator.telemetry.gaps")}
        value={count(summary.gaps)}
        tone={summary.gaps ? "warn" : undefined}
      />
      <Cell
        label={t("operator.telemetry.anomalies")}
        value={count(summary.anomalies)}
        hint={summary.commands ? t("operator.telemetry.commands_count", { count: summary.commands }) : undefined}
        tone={summary.anomalies_error ? "error" : summary.anomalies ? "warn" : undefined}
      />
    </div>
  );
}
